import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FilterOption } from '@/types/filters.types';

interface FilterChecklistProps {
  value: string[];
  options: FilterOption[];
  onChange: (next: string[]) => void;
  /** Whether the filter is currently in negative (NOT IN) mode. */
  negate?: boolean;
  /** When omitted, the +/- toggle is hidden. */
  onNegateChange?: (next: boolean) => void;
  /** When true, render a substring search box above the checklist. */
  searchable?: boolean;
  /** Max height for the option list before it scrolls. */
  maxHeightClass?: string;
  /**
   * When true, enables legacy-style grouped select behavior:
   * - Options whose value starts with "filtersgroup-" are treated as group parents
   * - Checking a parent selects all items in that group
   * - Unchecking any child deselects the parent but keeps other children
   *
   * When combined with `collapsibleGroups` the cascade is dropped entirely and
   * the parent becomes an independent choice — see below.
   */
  groupSelect?: boolean;
  /**
   * When true (and options carry a `group`), render categories as collapsible
   * headers: the list starts showing only category names and clicking a
   * category expands its options. Mirrors the legacy Follow That Patient
   * product filter.
   *
   * Combined with `groupSelect` (the Lead Source filter), the group's own
   * `filtersgroup-<id>` option is promoted onto the header as an INDEPENDENT
   * checkbox: it selects the category itself and does not tick, untick, or
   * roll up its children. That is required, not a preference — the underlying
   * record stores the category and the source in separate columns and may
   * carry either one without the other, so neither selection is a subset of
   * the other. A category with no sources at all is still selectable.
   */
  collapsibleGroups?: boolean;
}

/**
 * Sentinel prefix marking an option as its group's parent. Kept in sync with
 * LEAD_SOURCE_GROUP_PREFIX in hooks/filters/leadSourceOptions.ts.
 */
const GROUP_PARENT_PREFIX = 'filtersgroup-';

const isGroupParentValue = (value: string) => value.startsWith(GROUP_PARENT_PREFIX);

/**
 * Inline checkbox list with a +/- negate toggle, "Select all", and "Clear"
 * actions. Mirrors legacy `FiltersCheckList::buildFilterCheckList` from
 * `html/html/lib/filters/FiltersCheckList.php` so users get the same UX
 * (always-visible checkboxes, toggleable +/- pill) as the legacy app.
 */
export function FilterChecklist({
  value,
  options,
  onChange,
  negate = false,
  onNegateChange,
  searchable = false,
  maxHeightClass = 'max-h-56',
  groupSelect = false,
  collapsibleGroups = false,
}: FilterChecklistProps) {
  const [search, setSearch] = useState('');
  const searchTerm = search.trim();
  const selected = useMemo(() => new Set(value), [value]);

  const hasGroups = groupSelect || options.some((o) => o.group);
  const useCollapsible = collapsibleGroups && options.some((o) => o.group);
  const [sortSnapshot, setSortSnapshot] = useState<Set<string>>(() => new Set(value));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) {
      setSortSnapshot(new Set(value));
    }
  }, [value]);

  const updateSnapshot = useCallback(() => {
    setSortSnapshot(new Set(value));
  }, [value]);

  const filteredOptions = useMemo(() => {
    const base = options.filter((opt) => !/----/.test(opt.label ?? ''));
    const term = search.trim().toLowerCase();
    if (!term) return base;
    // Match on the option label OR its category/group name, so searching a
    // category surfaces every product within it (not just products whose own
    // name contains the term).
    return base.filter(
      (opt) =>
        (opt.label ?? '').toLowerCase().includes(term) ||
        (opt.group ?? '').toLowerCase().includes(term),
    );
  }, [options, search]);

  const displayOptions = useMemo(() => {
    if (hasGroups || search.trim()) return filteredOptions;
    const snap = sortSnapshot;
    return [...filteredOptions].sort((a, b) => {
      const aSnap = snap.has(a.value) ? 0 : 1;
      const bSnap = snap.has(b.value) ? 0 : 1;
      if (aSnap !== bSnap) return aSnap - bSnap;
      return (a.label ?? '').localeCompare(b.label ?? '');
    });
  }, [filteredOptions, sortSnapshot, hasGroups, search]);

  /** Plain add/remove of a single value, with no group cascade. */
  const toggleValue = (optionValue: string) => {
    if (selected.has(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const toggle = (optionValue: string) => {
    if (!groupSelect) {
      toggleValue(optionValue);
      return;
    }

    const isParent = isGroupParentValue(optionValue);
    const opt = options.find((o) => o.value === optionValue);
    const group = opt?.group;

    if (isParent && group) {
      if (selected.has(optionValue)) {
        const groupValues = new Set(
          options.filter((o) => o.group === group).map((o) => o.value),
        );
        onChange(value.filter((v) => !groupValues.has(v)));
      } else {
        const groupValues = options
          .filter((o) => o.group === group)
          .map((o) => o.value);
        const merged = new Set([...value, ...groupValues]);
        onChange([...merged]);
      }
    } else if (group) {
      const parentValue = options.find(
        (o) => o.group === group && isGroupParentValue(o.value),
      )?.value;
      if (selected.has(optionValue)) {
        let next = value.filter((v) => v !== optionValue);
        if (parentValue) next = next.filter((v) => v !== parentValue);
        onChange(next);
      } else {
        onChange([...value, optionValue]);
      }
    } else {
      if (selected.has(optionValue)) {
        onChange(value.filter((v) => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
    }
  };

  /**
   * Toggle every currently-visible option belonging to `group` (the clickable
   * category header path used when options carry `opt.group` but `groupSelect`
   * is off). If all visible members are already selected, deselect them;
   * otherwise select the ones that aren't. Only visible (search-filtered)
   * members are affected so behavior matches what the user sees.
   */
  const toggleGroup = (group: string) => {
    const members = displayOptions
      .filter((o) => o.group === group)
      .map((o) => o.value);
    if (members.length === 0) return;
    const allSelected = members.every((v) => selected.has(v));
    if (allSelected) {
      const remove = new Set(members);
      onChange(value.filter((v) => !remove.has(v)));
    } else {
      onChange([...new Set([...value, ...members])]);
    }
  };

  /**
   * Group name → its parent option value (when it has one) and its full leaf
   * membership. Built from the *unfiltered* options so an active search can't
   * hide the parent row or throw the child counts off.
   */
  const groupMembership = useMemo(() => {
    const map = new Map<string, { parentValue?: string; leafValues: string[] }>();
    for (const opt of options) {
      if (!opt.group) continue;
      const entry = map.get(opt.group) ?? { leafValues: [] };
      if (isGroupParentValue(opt.value)) entry.parentValue = opt.value;
      else entry.leafValues.push(opt.value);
      map.set(opt.group, entry);
    }
    return map;
  }, [options]);

  // ─── Collapsible categories (legacy product filter) ───────────────────
  // Ordered, de-duplicated list of category names in display order.
  const groupNames = useMemo(() => {
    const seen: string[] = [];
    for (const opt of displayOptions) {
      const g = opt.group ?? '';
      if (g && !seen.includes(g)) seen.push(g);
    }
    return seen;
  }, [displayOptions]);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const didInitExpand = useRef(false);

  /**
   * Categories the user explicitly collapsed *while a search is active*.
   *
   * Searching force-opens every matching category so results are never hidden
   * behind a closed header, but that must not leave the chevron dead: without
   * this, a click updated `expandedGroups` and `isGroupExpanded` ignored it.
   * Kept separate from `expandedGroups` so collapsing something mid-search
   * doesn't rearrange the list the user goes back to when they clear the box.
   * Reset on every new term, so each search starts open again.
   */
  const [searchCollapsed, setSearchCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSearchCollapsed(new Set());
  }, [searchTerm]);

  // Seed once (after async options arrive): expand categories that already
  // contain a selected child so the user's existing picks are visible. A
  // category selected on its own is already visible on its header row, so it
  // does not force its children open.
  useEffect(() => {
    if (!useCollapsible || didInitExpand.current || options.length === 0) return;
    const withSelection = new Set<string>();
    for (const opt of options) {
      if (opt.group && !isGroupParentValue(opt.value) && selected.has(opt.value)) {
        withSelection.add(opt.group);
      }
    }
    setExpandedGroups(withSelection);
    didInitExpand.current = true;
  }, [useCollapsible, options, selected]);

  const toggleExpand = (group: string) => {
    const setter = searchTerm ? setSearchCollapsed : setExpandedGroups;
    setter((current) => {
      const next = new Set(current);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  // While searching, every matching category starts open so results are seen —
  // but stays collapsible.
  const isGroupExpanded = (group: string) =>
    searchTerm ? !searchCollapsed.has(group) : expandedGroups.has(group);

  const isSearching = search.trim().length > 0;

  /**
   * Values the user can actually see right now. In collapsible mode a search
   * force-expands every matching category (see `isGroupExpanded`), so the
   * search-filtered set is exactly what is on screen.
   */
  const visibleValues = useMemo(
    () => filteredOptions.map((opt) => opt.value),
    [filteredOptions],
  );

  /**
   * With a search term active, "Select all" applies only to the matching
   * options and MERGES into the existing selection — searching for 20 of 200
   * and hitting it must not silently drop the picks the search is hiding.
   *
   * Unsearched it selects everything, categories AND sources. That is not
   * reducible to the category values alone: a category and its sources are
   * independent columns, so a record carrying a source with no category would
   * be missed.
   */
  const selectAll = () => {
    if (isSearching) {
      onChange([...new Set([...value, ...visibleValues])]);
      return;
    }
    onChange(options.map((opt) => opt.value));
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <div
      className={cn(
        'rounded-md border border-slate-200 bg-white',
        negate && 'border-red-200 bg-red-50/30',
      )}
      onFocus={() => { focusedRef.current = true; }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          focusedRef.current = false;
          updateSnapshot();
        }
      }}
    >
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-2 py-1.5">
        {onNegateChange ? (
          <button
            type="button"
            title="Toggle negative / positive filter"
            onClick={() => onNegateChange(!negate)}
            className={cn(
              'inline-flex h-5 select-none overflow-hidden rounded border text-[11px] font-semibold leading-none',
              'border-slate-200 bg-white',
            )}
          >
            <span
              className={cn(
                'flex h-full w-5 items-center justify-center transition-colors',
                !negate ? 'bg-blue-600 text-white' : 'text-slate-400',
              )}
            >
              +
            </span>
            <span
              className={cn(
                'flex h-full w-5 items-center justify-center transition-colors',
                negate ? 'bg-red-600 text-white' : 'text-slate-400',
              )}
            >
              -
            </span>
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2 text-[11px] text-slate-600">
          <button
            type="button"
            onClick={selectAll}
            disabled={isSearching && visibleValues.length === 0}
            title={
              isSearching
                ? `Add the ${visibleValues.length} matching option${visibleValues.length === 1 ? '' : 's'} to the selection`
                : 'Select every option'
            }
            className="font-medium hover:text-blue-600 disabled:cursor-not-allowed disabled:text-slate-300"
          >
            {isSearching ? `Select ${visibleValues.length}` : 'Select all'}
          </button>
          <span className="text-slate-300">|</span>
          <button
            type="button"
            onClick={clearAll}
            className="font-medium hover:text-slate-800"
          >
            Clear
          </button>
        </div>
      </div>

      {searchable && (
        <div className="px-2 pb-1 pt-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full rounded border border-slate-200 bg-white py-1 pl-7 pr-2 text-xs outline-none focus:border-blue-400"
            />
          </div>
        </div>
      )}

      <ul className={cn('overflow-y-auto px-1 py-1', maxHeightClass)}>
        {displayOptions.length === 0 ? (
          <li className="px-2 py-2 text-center text-[11px] text-slate-400">
            {options.length === 0 ? 'No options available' : 'No matches'}
          </li>
        ) : useCollapsible ? (
          groupNames.map((groupName) => {
            // When the category has its own `filtersgroup-` option, promote it
            // out of the list: it becomes the header checkbox, and only the
            // real leaves are rendered as children. Read from the unfiltered
            // membership so an active search can't hide the parent.
            const membership = groupMembership.get(groupName);
            const parentValue = groupSelect ? membership?.parentValue : undefined;
            const members = displayOptions.filter(
              (o) => o.group === groupName && o.value !== parentValue,
            );
            const memberValues = members.map((o) => o.value);
            // With a real parent option the category is an INDEPENDENT choice,
            // not an umbrella over its children: the record it matches may
            // carry only the category (plans.referralGeneral with no
            // referralSpecific), and a child may be set with no category at
            // all. So the header checkbox reflects only the category's own
            // state — never a roll-up — and there is no indeterminate state.
            const parentSelected = parentValue ? selected.has(parentValue) : false;
            const groupAll = parentValue
              ? parentSelected
              : memberValues.length > 0 && memberValues.every((v) => selected.has(v));
            const groupSome = parentValue
              ? false
              : memberValues.some((v) => selected.has(v));
            // Surfaced on the header so selections inside a collapsed category
            // aren't invisible.
            const selectedChildCount = parentValue
              ? (membership?.leafValues ?? []).filter((v) => selected.has(v)).length
              : 0;
            const expandable = members.length > 0;
            const expanded = expandable && isGroupExpanded(groupName);
            return (
              <li key={`group-${groupName}`} className="mt-1 first:mt-0">
                <div
                  className={cn(
                    'flex items-center gap-1 rounded bg-slate-50',
                    'hover:bg-slate-100',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggleExpand(groupName)}
                    className="flex min-w-0 flex-1 items-center gap-1 px-2 py-1 text-left"
                    aria-expanded={expandable ? expanded : undefined}
                    disabled={!expandable}
                    title={
                      !expandable
                        ? `${groupName} has no sources`
                        : expanded
                          ? `Collapse ${groupName}`
                          : `Expand ${groupName}`
                    }
                  >
                    <ChevronDown
                      className={cn(
                        'h-3 w-3 shrink-0 transition-transform',
                        !expanded && '-rotate-90',
                        expandable ? 'text-slate-400' : 'invisible',
                      )}
                    />
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {groupName}
                    </span>
                    {!expanded && selectedChildCount > 0 && (
                      <span
                        className={cn(
                          'ml-1 shrink-0 rounded px-1 text-[10px] font-semibold leading-4',
                          negate ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700',
                        )}
                        title={`${selectedChildCount} selected in ${groupName}`}
                      >
                        {selectedChildCount}
                      </span>
                    )}
                  </button>
                  <label
                    className="flex shrink-0 cursor-pointer items-center px-2 py-1"
                    title={
                      parentValue
                        ? `Match the ${groupName} category itself`
                        : `Select or clear all in ${groupName}`
                    }
                  >
                    <input
                      type="checkbox"
                      checked={groupAll}
                      ref={(el) => {
                        if (el) el.indeterminate = groupSome && !groupAll;
                      }}
                      onChange={() =>
                        parentValue ? toggleValue(parentValue) : toggleGroup(groupName)
                      }
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 rounded border-slate-300',
                        negate ? 'text-red-600 focus:ring-red-500' : 'text-blue-600 focus:ring-blue-500',
                      )}
                    />
                  </label>
                </div>
                {expanded &&
                  members.map((opt) => {
                    // Never derived from the parent — see the note above.
                    const isChecked = selected.has(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className={cn(
                          'flex cursor-pointer items-start gap-2 rounded px-2 py-1 pl-6 text-xs transition-colors',
                          'hover:bg-slate-50',
                          isChecked && (negate ? 'bg-red-50' : 'bg-blue-50/60'),
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() =>
                            parentValue ? toggleValue(opt.value) : toggle(opt.value)
                          }
                          className={cn(
                            'mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-slate-300',
                            negate ? 'text-red-600 focus:ring-red-500' : 'text-blue-600 focus:ring-blue-500',
                          )}
                        />
                        <span className="break-words text-slate-700">{opt.label}</span>
                      </label>
                    );
                  })}
              </li>
            );
          })
        ) : (
          displayOptions.map((opt, idx) => {
            const isChecked = selected.has(opt.value);
            const isGroupParent = groupSelect && opt.value.startsWith('filtersgroup-');
            const showGroupHeader =
              !groupSelect &&
              opt.group &&
              (idx === 0 || displayOptions[idx - 1].group !== opt.group);
            return (
              <li key={opt.value}>
                {showGroupHeader && opt.group && (() => {
                  const groupName = opt.group;
                  const members = displayOptions
                    .filter((o) => o.group === groupName)
                    .map((o) => o.value);
                  const groupAll =
                    members.length > 0 && members.every((v) => selected.has(v));
                  const groupSome = members.some((v) => selected.has(v));
                  return (
                    <label
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded px-2 py-1 mt-1 first:mt-0',
                        'text-[11px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-50',
                        'hover:bg-slate-100',
                      )}
                      title={`Select or clear all in ${groupName}`}
                    >
                      <input
                        type="checkbox"
                        checked={groupAll}
                        ref={(el) => {
                          if (el) el.indeterminate = groupSome && !groupAll;
                        }}
                        onChange={() => toggleGroup(groupName)}
                        className={cn(
                          'h-3.5 w-3.5 shrink-0 rounded border-slate-300',
                          negate ? 'text-red-600 focus:ring-red-500' : 'text-blue-600 focus:ring-blue-500',
                        )}
                      />
                      <span>{groupName}</span>
                    </label>
                  );
                })()}
                <label
                  className={cn(
                    'flex cursor-pointer items-start gap-2 rounded px-2 py-1 text-xs transition-colors',
                    'hover:bg-slate-50',
                    isChecked && (negate ? 'bg-red-50' : 'bg-blue-50/60'),
                    isGroupParent && 'mt-1 first:mt-0 font-semibold text-slate-800',
                    !isGroupParent && groupSelect && opt.group && 'pl-6',
                    !groupSelect && opt.group && 'pl-4',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggle(opt.value)}
                    className={cn(
                      'mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-slate-300',
                      negate ? 'text-red-600 focus:ring-red-500' : 'text-blue-600 focus:ring-blue-500',
                    )}
                  />
                  <span className={cn('break-words', isGroupParent ? 'text-slate-800' : 'text-slate-700')}>
                    {isGroupParent ? opt.group : opt.label}
                  </span>
                </label>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
